"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from "@/components/ui/card";
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  ExternalLink,
  Check,
  X,
  ChevronDown,
  Map,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import { useAnalytics } from "@/components/providers/analytics-provider";
import { useAtomValue } from "jotai";
import { businessItemsAtom } from "@/store";
import { BusinessListingItem } from "@/lib/data/applicators";
import {
  useBusinessHours,
  formatIntervals,
  dayName,
} from "@/hooks/use-business-hours";
import { Badge } from "@/components/ui/badge";
import type { Map as LeafletMap } from "leaflet";

// Builds a robust Google Maps URL. For a known place_id we follow the Maps URLs API pattern:
// https://www.google.com/maps/search/?api=1&query=<NAME>&query_place_id=<PLACE_ID>
// This reliably resolves even when the plain place_id search fails. Falls back to name+city search.
function buildMapsUrl(b: BusinessListingItem) {
  if (b.google_place_id) {
    const encodedName = encodeURIComponent(b.name);
    return `https://www.google.com/maps/search/?api=1&query=${encodedName}&query_place_id=${b.google_place_id}`;
  }
  const query = encodeURIComponent(`${b.name} ${b.city} ${b.state}`);
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
}

// Check if business has a specific specialty (case-insensitive partial match)
function hasSpecialty(specialties: string[], keyword: string): boolean {
  const lower = keyword.toLowerCase();
  return specialties.some((s) => s.toLowerCase().includes(lower));
}

export function BusinessCard({
  business,
  index: propIndex,
  isFeatured,
  citySlug,
}: {
  business: BusinessListingItem;
  index?: number;
  isFeatured?: boolean;
  citySlug: string;
}) {
  const [showMapExpanded, setShowMapExpanded] = useState(false);
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);
  const mapsUrl = buildMapsUrl(business);
  const hours = useBusinessHours(business);

  // Get index from Jotai store if not provided as prop
  const businessItems = useAtomValue(businessItemsAtom);
  const businessItem = businessItems.find((item) => item.id === business.id);
  const index = propIndex ?? businessItem?.index;

  // Determine if business is currently open
  const isOpen = hours.today && hours.today.intervals.length > 0;
  const statusText = isOpen ? "Open Now" : "Closed";

  // Hard-coded specialties to check with service flags
  const specialtyStatus = [
    {
      name: "microcement",
      has:
        business.microcement_services ||
        hasSpecialty(business.specialties, "microcement"),
    },
    {
      name: "plaster",
      has:
        business.plaster_services ||
        hasSpecialty(business.specialties, "plaster"),
    },
    {
      name: "venetian plaster",
      has:
        business.venetian_plaster_services ||
        hasSpecialty(business.specialties, "venetian plaster"),
    },
    {
      name: "limewash",
      has:
        business.limewash_services ||
        hasSpecialty(business.specialties, "limewash"),
    },
    {
      name: "paint",
      has:
        business.paint_services || hasSpecialty(business.specialties, "paint"),
    },
  ];

  const hardCodedSpecialties = specialtyStatus.map((s) => s.name);

  // Get remaining specialties (excluding the hard-coded ones from display)
  const remainingSpecialties = business.specialties.filter(
    (s) =>
      !hardCodedSpecialties.some((hc) =>
        s.toLowerCase().includes(hc.toLowerCase())
      )
  );

  const displayedSpecialties = remainingSpecialties.slice(0, 6);
  const hiddenSpecialtiesCount = remainingSpecialties.length - 6;

  const { trackTyped } = useAnalytics();

  return (
    <Card
      className={`bg-card shadow-lg h-full overflow-hidden pb-0 p-0 ${
        isFeatured ? "border-2 border-green-600" : "border-border"
      }`}
    >
      {/* Header */}
      <CardHeader className="pb-4 pt-4 bg-foreground/5 border-b">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            {/* Business Name with Number Badge */}
            <div className="flex items-center gap-3 mb-2">
              {index !== undefined && (
                <div className="shrink-0 w-7 h-7 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm shadow-md border-2 border-white">
                  {index}
                </div>
              )}
              <h2 className="font-serif text-2xl font-semibold tracking-tight">
                {business.name}
              </h2>
            </div>

            {/* Address */}
            <div className="flex items-start text-muted-foreground mb-2 text-sm">
              <MapPin className="h-4 w-4 mr-2 shrink-0 mt-0.5" />
              <span>
                {business.address_line1 && (
                  <>
                    {business.address_line1}
                    {business.address_line2 && `, ${business.address_line2}`}
                    <br />
                  </>
                )}
                {business.city}, {business.state} {business.postal_code || ""}
              </span>
            </div>

            {/* Open/Closed Status */}
            <div className="flex items-center gap-2">
              <Badge
                variant={isOpen ? "default" : "secondary"}
                className={isOpen ? "bg-green-600 hover:bg-green-700" : ""}
              >
                <Clock className="h-3 w-3 mr-1" />
                {statusText}
              </Badge>
            </div>
          </div>

          {/* Right side: Featured tag + Rating & Reviews */}
          <div className="flex items-center gap-4 text-sm">
            {isFeatured && (
              <span className="inline-flex items-center rounded-md bg-green-600 text-white px-3 py-1 text-xs font-bold uppercase tracking-wide">
                Featured
              </span>
            )}
            {business.rating !== null && business.rating !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-yellow-500 text-lg">★</span>
                <span className="font-semibold text-foreground">
                  {business.rating.toFixed(1)}
                </span>
              </div>
            )}
            {business.review_count > 0 && (
              <span className="text-muted-foreground">
                ({business.review_count} review
                {business.review_count !== 1 ? "s" : ""})
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Main Body - 3 Sections in Grid */}
      <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* 1. Contact Section */}
        <div>
          <h3 className="text-sm font-semibold mb-3 text-foreground">
            Contact
          </h3>
          <div className="space-y-2">
            {business.website && (
              <div className="flex items-start text-muted-foreground p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors">
                <Globe className="h-4 w-4 mr-2 shrink-0 mt-0.5" />
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:text-foreground transition-colors truncate whitespace-nowrap overflow-hidden max-w-[180px]"
                  title={business.website}
                  onClick={() =>
                    trackTyped("applicators_business_website_click", {
                      business_id: business.id,
                      city_slug: citySlug,
                    })
                  }
                >
                  {business.website.replace(/^https?:\/\/(www\.)?/, "")}
                </a>
              </div>
            )}
            {business.phone && (
              <div className="flex items-center text-muted-foreground p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors">
                <Phone className="h-4 w-4 mr-2 shrink-0" />
                <a
                  href={`tel:${business.phone}`}
                  className="hover:text-foreground transition-colors text-sm"
                  onClick={() =>
                    trackTyped("applicators_business_phone_click", {
                      business_id: business.id,
                      city_slug: citySlug,
                    })
                  }
                >
                  {business.phone}
                </a>
              </div>
            )}
            {business.email && (
              <div className="flex items-start text-muted-foreground p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors">
                <Mail className="h-4 w-4 mr-2 shrink-0 mt-0.5" />
                <a
                  href={`mailto:${business.email}`}
                  className="hover:text-foreground transition-colors wrap-break-word text-sm"
                  onClick={() =>
                    trackTyped("applicators_business_email_click", {
                      business_id: business.id,
                      city_slug: citySlug,
                    })
                  }
                >
                  {business.email}
                </a>
              </div>
            )}
            <div className="flex items-start text-muted-foreground p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors">
              <ExternalLink className="h-4 w-4 mr-2 shrink-0 mt-0.5" />
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground transition-colors text-sm"
                onClick={() =>
                  trackTyped("applicators_business_maps_click", {
                    business_id: business.id,
                    city_slug: citySlug,
                  })
                }
              >
                View on Google Maps
              </a>
            </div>
          </div>
        </div>

        {/* 2. Specialties Column 1 - Required Specialties */}
        <div>
          <h3 className="text-sm font-semibold mb-3 text-foreground">
            Specialties
          </h3>

          {/* Hard-coded specialties with checkmarks/crosses */}
          <div className="space-y-2">
            {specialtyStatus.map(({ name, has }) => (
              <div
                key={name}
                className="flex items-center gap-2 p-2 rounded-md bg-muted/30"
              >
                {has ? (
                  <div className="inline-flex h-5 w-5 flex-none shrink-0 items-center justify-center rounded-full bg-green-600 text-white shadow-sm">
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </div>
                ) : (
                  <div className="inline-flex h-5 w-5 flex-none shrink-0 items-center justify-center rounded-full bg-gray-300 text-gray-600 shadow-sm">
                    <X className="h-3.5 w-3.5" strokeWidth={3} />
                  </div>
                )}
                <span className="capitalize font-medium text-sm">{name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 3. Specialties Column 2 - Additional Specialties */}
        <div>
          <h3 className="text-sm font-semibold mb-3 text-foreground">
            Additional Services
          </h3>

          {/* Additional specialties (up to 6) */}
          {displayedSpecialties.length > 0 ? (
            <>
              <div className="space-y-2">
                {displayedSpecialties.map((specialty) => (
                  <div
                    key={specialty}
                    className="flex items-center gap-2 p-2 rounded-md bg-muted/30"
                  >
                    <div className="inline-flex h-5 w-5 flex-none shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </div>
                    <span className="capitalize text-sm">{specialty}</span>
                  </div>
                ))}
              </div>

              {/* Show More Dialog */}
              {hiddenSpecialtiesCount > 0 && (
                <Dialog
                  open={showAllSpecialties}
                  onOpenChange={setShowAllSpecialties}
                >
                  <DialogTrigger asChild>
                    <Button variant="link" className="px-0 h-auto text-xs mt-2">
                      +{hiddenSpecialtiesCount} more
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>All Additional Services</DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="max-h-96 pr-4">
                      <div className="space-y-2">
                        {remainingSpecialties.map((specialty) => (
                          <div
                            key={specialty}
                            className="flex items-center gap-2 text-sm"
                          >
                            <div className="inline-flex h-5 w-5 flex-none shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm">
                              <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            </div>
                            <span className="capitalize">{specialty}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground p-2 rounded-md bg-muted/30">
              No additional services listed
            </p>
          )}
        </div>

        {/* 4. Store Hours Section */}
        <div>
          <h3 className="text-sm font-semibold mb-3 text-foreground">
            Store Hours
          </h3>
          {hours.weekly.length ? (
            <ul className="space-y-1.5">
              {hours.weekly.map((d) => (
                <li
                  key={d.day}
                  className={`flex justify-between p-2 rounded-md ${
                    d.day === new Date().getDay()
                      ? "font-semibold text-foreground bg-muted/50"
                      : "text-muted-foreground bg-muted/30"
                  }`}
                >
                  <span className="text-sm">{dayName(d.day)}</span>
                  <span className="text-sm">
                    {formatIntervals(d.intervals)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground p-2 rounded-md bg-muted/30">
              Hours not available
            </p>
          )}
        </div>
      </CardContent>

      {/* Footer with expandable map */}
      <CardFooter className="flex-col p-0">
        <Button
          variant="ghost"
          className="w-full rounded-none border-t"
          onClick={() => setShowMapExpanded(!showMapExpanded)}
        >
          <Map className="h-4 w-4 mr-2" />
          {showMapExpanded ? "Hide Map" : "Show Map"}
          <ChevronDown
            className={`h-4 w-4 ml-2 transition-transform ${
              showMapExpanded ? "rotate-180" : ""
            }`}
          />
        </Button>

        {showMapExpanded && business.latitude && business.longitude && (
          <BusinessMap
            latitude={business.latitude}
            longitude={business.longitude}
            name={business.name}
            index={index}
          />
        )}
      </CardFooter>
    </Card>
  );
}

// Simple single-location OpenStreetMap component
function BusinessMap({
  latitude,
  longitude,
  name,
  index,
}: {
  latitude: number;
  longitude: number;
  name: string;
  index?: number;
}) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const [ready, setReady] = useState(false);

  // Load Leaflet dynamically on client only
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const mod = await import("leaflet");
      if (cancelled) return;
      const L = mod.default ?? mod;

      // Inject stylesheet once
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css";
        link.rel = "stylesheet";
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }

      // Patch default marker icons (CDN) once
      type IconDefaultPatched = typeof L.Icon.Default & {
        __PLASTER_MARKER_PATCHED__?: boolean;
      };
      if (!(L.Icon.Default as IconDefaultPatched).__PLASTER_MARKER_PATCHED__) {
        (L.Icon.Default as IconDefaultPatched).__PLASTER_MARKER_PATCHED__ =
          true;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl:
            "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
          iconUrl:
            "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
          shadowUrl:
            "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        });
      }
      setReady(true);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize map after Leaflet is ready
  useEffect(() => {
    if (!ready || !mapContainerRef.current || mapRef.current) return;

    async function initMap() {
      const L = await import("leaflet").then((mod) => mod.default ?? mod);
      if (!mapContainerRef.current) return;

      const map = L.map(mapContainerRef.current, {
        center: [latitude, longitude],
        zoom: 15,
        attributionControl: true,
        zoomControl: true,
      });

      mapRef.current = map;

      // Add OpenStreetMap tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        minZoom: 2,
        maxZoom: 19,
      }).addTo(map);

      // Generate numbered circle SVG icon
      const numberedCircleSvg = (num: number) => {
        return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'><circle cx='14' cy='14' r='13' fill='#e3342f' stroke='white' stroke-width='2'/><text x='14' y='18' text-anchor='middle' font-size='15' font-family='Arial,Helvetica,sans-serif' font-weight='bold' fill='white'>${num}</text></svg>`;
      };

      // Add marker with custom numbered icon
      const markerIcon =
        index !== undefined
          ? L.icon({
              iconUrl: `data:image/svg+xml;utf8,${encodeURIComponent(
                numberedCircleSvg(index)
              )}`,
              iconSize: [28, 28],
              iconAnchor: [14, 28],
              popupAnchor: [0, -28],
              shadowUrl: undefined,
            })
          : undefined;

      const marker = markerIcon
        ? L.marker([latitude, longitude], { icon: markerIcon })
        : L.marker([latitude, longitude]);

      marker.addTo(map).bindPopup(`<strong>${name}</strong>`).openPopup();
    }

    initMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [ready, latitude, longitude, name, index]);

  return (
    <div className="w-full h-[300px] border-t relative">
      <div ref={mapContainerRef} className="w-full h-full" />
    </div>
  );
}
